import {
  createViewAction,
  formatDateTime,
  prepareAttachmentsPost,
} from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { Button } from "antd";
import { URL_CONTACT_US } from "../../utils/urls";
import { useGlobalContext } from "../../context/GlobalContext";
import { CLOSED, RESOLVED } from "../../utils/constants";
import useSet from "../common/useSet";
import { API_ENQUIRIES } from "../../utils/apis";
import { useToasts } from "../common/useToasts";
import { StatusTag } from "../../components/common/registerUtils";
import { BASE_API_URL } from "../../config";

export const useEnquiries = () => {
  const { updateData } = useGlobalContext();

  const handleViewAttachment = (url: string) => {
    if (url) {
      console.log("Url", url);
      updateData({ openAttachment: true, selectedAttachment: url });
    } else {
      console.warn("No attachment URL provided");
    }
  };

  const tableColumns = () => [
    {
      id: "ticket",
      header: "Ticket Number",
    },
    {
      id: "full_name",
      header: "Full Name",
    },
    {
      id: "email",
      header: "Email",
    },
    {
      id: "category",
      header: "Category",
    },
    {
      id: "details",
      header: "Details",
    },
    {
      id: "status",
      header: "Status",
    },
    {
      id: "created_at",
      header: "Created At",
    },
    {
      id: "created_by",
      header: "Created By",
    },
    {
      id: "actions",
      header: "Actions",
    },
  ];

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      const row = {
        ...item,
        created_at: formatDateTime(item.created_at),
        created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
        email: item?.email,
        status: <StatusTag status={item?.status} />,
        actions: (
          <FormActions actions={[createViewAction(URL_CONTACT_US, item?.id)]} />
        ),
      };
      return row;
    }),
  ];

  const viewData = (item: any) => {
    return {
      sections: [
        {
          title: "Enquiries Details",
          fields: {
            id: item?.id,
            full_name: item?.full_name,
            category: item?.category,
            details: item?.details,
            email: item?.email,
            ticket: item?.ticket,
            status: <StatusTag status={item?.status} />,
            created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
            created_at: formatDateTime(item?.created_at),
          },
        },

        ...(item?.contact_us_attachments?.document_upload
          ? [
              {
                title: "Upload Documents",
                fields: {
                  attachments: (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {item?.contact_us_attachments?.document_upload?.map(
                        (doc) => (
                          <Button
                            type="primary"
                            key={doc?.file}
                            style={{
                              marginBottom: 4,
                              width: "fit-content",
                              paddingLeft: 10,
                              paddingRight: 10,
                            }}
                            size="large"
                            onClick={() =>
                              handleViewAttachment(
                                `${BASE_API_URL.replace("/api/", "")}${doc?.file}`,
                              )
                            }
                          >
                            View
                          </Button>
                        ),
                      )}
                    </div>
                  ),
                },
              },
            ]
          : []),
        ...(item?.contact_us_attachments?.document_response
          ? [
              {
                title: "Response Documents",
                fields: {
                  attachments: (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {item?.contact_us_attachments?.document_response?.map(
                        (doc) => (
                          <Button
                            type="primary"
                            key={doc?.file}
                            style={{
                              marginBottom: 4,
                              width: "fit-content",
                              paddingLeft: 10,
                              paddingRight: 10,
                            }}
                            size="large"
                            onClick={() =>
                              handleViewAttachment(
                                `${BASE_API_URL.replace("/api/", "")}${doc?.file}`,
                              )
                            }
                          >
                            View
                          </Button>
                        ),
                      )}
                    </div>
                  ),
                },
              },
            ]
          : []),
      ],
    };
  };

  const tableTitle = "Enquiries";
  return {
    tableColumns,
    tableData,
    viewData,
    tableTitle,
  };
};

export const useEnquiriesActions = ({ form, data }: any) => {
  const { uploadData, success, error, submitting } = useSet();

  useToasts({
    success,
    error,
    submitting,
    redirect: "reload",
    successMessage: "Enquiries status updated",
  });

  const fields = [
    {
      field_name: "action",
      label: "Action",
      type: "select",
      options_data: [
        { name: "Resolve", id: RESOLVED },
        { name: "Close", id: CLOSED },
      ],
      props: { required: true },
    },
    {
      field_name: "comment",
      label: "Comment",
      type: "textarea",
      props: { required: true },
    },
    {
      field_name: "attachments",
      type: "file",
      label: "Attach Images",
      description: "Upload any relevant images (optional).",
      props: { multiple: true, accept: "image/*" },
    },
  ];

  function onSubmit(payload: any) {
    if (!data) return null;
    return uploadData({
      api: `${API_ENQUIRIES}${data?.id}/`,
      method: "PATCH",
      params: {
        status: payload.action,
        reason: payload.comment,
        ...prepareAttachmentsPost([form?.attachments], "attachments", {
          document_type: "other",
          caption: "document_response",
        }),
      },
      type: "multipart/form-data",
    });
  }

  const enableSubmit = !!form?.action && !!form?.comment;

  return {
    fields,
    onSubmit,
    enableSubmit,
    finalStates: [RESOLVED, CLOSED],
  };
};
